import { inject } from '@angular/core';
import { signalStoreFeature, withMethods, withProps, getState } from '@ngrx/signals';
import { StorageService } from '../../../services/storage.service';
import { BillAiFlowState } from './bill-ai-flow.state';

/**
 * Custom SignalStore feature for persisting bill AI flow state to localStorage.
 *
 * Persistence is explicit — callers invoke saveState() after mutations.
 * Only saves when matchRequest exists (no point persisting empty intro state).
 */
export function withPersistence() {
  return signalStoreFeature(
    withProps(() => ({
      _storageService: inject(StorageService),
      _storageKey: null as string | null,
    })),
    withMethods((store) => {
      function doSave(): void {
        const key = (store as any)._storageKey;
        if (!key) return;

        const state = getState(store as any) as BillAiFlowState;
        if (!state.matchRequest) return; // Don't save empty state

        // Only save serializable state (exclude loading/error transients)
        const toSave = {
          currentStep: state.currentStep,
          completedSteps: state.completedSteps,
          currentBillIndex: state.currentBillIndex,
          matchRequest: state.matchRequest,
          matchResponse: state.matchResponse,
          customerMatch: state.customerMatch,
          selectedCustomerId: state.selectedCustomerId,
          selectedCustomerLabel: state.selectedCustomerLabel,
          treatAsNew: state.treatAsNew,
          createdCustomerUpfront: state.createdCustomerUpfront,
          newCustomerContact: state.newCustomerContact,
          newCustomerDetails: state.newCustomerDetails,
          entitySelections: state.entitySelections,
          selectionsDirtySinceMatch: state.selectionsDirtySinceMatch,
          meterEdits: state.meterEdits,
          saEdits: state.saEdits,
          billData: state.billData,
          billFileIdMapping: state.billFileIdMapping,
          mergedBills: state.mergedBills,
          billNavigation: state.billNavigation,
          duplicatesMap: state.duplicatesMap,
          duplicateResolutions: state.duplicateResolutions,
          rejectedPairSuggestions: state.rejectedPairSuggestions,
          entityNameOverrides: state.entityNameOverrides,
          lenMergeSelection: state.lenMergeSelection,
          saMergeSelection: state.saMergeSelection,
          meterMergeSelection: state.meterMergeSelection,
          previewBillFileId: state.previewBillFileId,
          fastTrackMode: state.fastTrackMode,
          fastTrackAnalysis: state.fastTrackAnalysis,
          fastTrackSkippedSteps: state.fastTrackSkippedSteps,
          fastTrackEnergyPlanChoice: state.fastTrackEnergyPlanChoice,
        };

        (store as any)._storageService.saveBillAiFlowState(key, toSave);
      }

      return {
      /**
       * Set the storage key for this bill AI flow instance.
       * Must be called during initialization.
       */
      setStorageKey(key: string): void {
        (store as any)._storageKey = key;
      },

      /**
       * Save the current state to localStorage immediately.
       */
      saveState(): void {
        doSave();
      },

      /**
       * Load saved state from localStorage.
       * Returns the saved state object or null if nothing found.
       */
      loadSavedState(): Partial<BillAiFlowState> | null {
        const key = (store as any)._storageKey;
        if (!key) return null;

        const saved = (store as any)._storageService.loadBillAiFlowState(key);
        if (!saved || !saved.matchRequest || !saved.currentStep) return null;

        return saved as Partial<BillAiFlowState>;
      },

      /**
       * Clear persisted bill AI flow state and the associated bill session.
       */
      clearPersistedState(): void {
        const key = (store as any)._storageKey;
        if (!key) return;
        (store as any)._storageService.clearBillAiFlowState(key);
        (store as any)._storageService.clearSession(key);
        // Null the key so no future saveState() calls can re-write after clear
        (store as any)._storageKey = null;
      },
    };}),
  );
}
